import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/toaster';
import Landing from './pages/Landing';
import Pricing from './pages/Pricing';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import AppLayout from './components/AppLayout';
import Dashboard from './pages/Dashboard';
import Players from './pages/Players';
import PlayerDetail from './pages/PlayerDetail';
import NewEvaluation from './pages/NewEvaluation';
import VideoLibrary from './pages/VideoLibrary';
import ProgressPage from './pages/ProgressPage';
import Reports from './pages/Reports';
import PlayerView from './pages/PlayerView';
import InviteView from './pages/InviteView';
import PublicClipView from './pages/PublicClipView';
import PublicProgressView from './pages/PublicProgressView';
import FeedbackAdmin from './pages/FeedbackAdmin';
import CheckoutSuccess from './pages/CheckoutSuccess';

function AppEntry() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  const activeSub = user.subscription && user.subscription.status === 'active';
  if (!activeSub) return <Navigate to="/pricing?resume=1" replace />;
  return <AppLayout />;
}

// Athlete-role users see PlayerView on the /app index, coaches see Dashboard.
function RoleIndex() {
  const { user } = useAuth();
  return user?.role === 'player' ? <PlayerView /> : <Dashboard />;
}

// Guard a child route so only certain roles can access it; others bounce to /app.
function RoleGuard({ allow, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (!allow.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

// Detect OAuth callback via #session_id in the URL and route to AuthCallback
// BEFORE any auth-gated route can bounce the user away.
function Router() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:token" element={<InviteView />} />
      <Route path="/clip/:token" element={<PublicClipView />} />
      <Route path="/progress/:token" element={<PublicProgressView />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/me" element={<PlayerView />} />

      <Route path="/app" element={<AppEntry />}>
        <Route index element={<RoleIndex />} />
        <Route path="players" element={<RoleGuard allow={['coach', 'scout']}><Players /></RoleGuard>} />
        <Route path="players/:id" element={<RoleGuard allow={['coach', 'scout']}><PlayerDetail /></RoleGuard>} />
        <Route path="evaluate/new" element={<NewEvaluation />} />
        <Route path="videos" element={<VideoLibrary />} />
        <Route path="progress" element={<ProgressPage />} />
        <Route path="reports" element={<RoleGuard allow={['coach', 'scout']}><Reports /></RoleGuard>} />
        <Route path="feedback" element={<RoleGuard allow={['coach', 'scout']}><FeedbackAdmin /></RoleGuard>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Router />
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
